import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { getPostseason, getPlayoffs } from "../lib/api";

const MiniGame = ({ g }) => {
  const homeWin = g.status === "final" && g.homeScore > g.awayScore;
  const awayWin = g.status === "final" && g.awayScore > g.homeScore;
  const Row = ({ abbr, name, logo, score, win }) => (
    <Link
      to={`/team/${abbr}`}
      className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition-colors duration-150"
    >
      <img src={logo} alt={abbr} className="w-6 h-6 object-contain" />
      <span className={`text-sm font-semibold truncate ${win ? "text-primary" : ""}`}>{name}</span>
      <span className={`ml-auto font-display text-xl tabular-nums ${win ? "text-primary" : "text-muted-foreground"}`}>
        {score}
      </span>
    </Link>
  );
  return (
    <div data-testid={`post-game-${g.id}`} className="border border-border bg-card">
      <Row abbr={g.awayTeam} name={g.awayName} logo={g.awayLogo} score={g.awayScore} win={awayWin} />
      <div className="border-t border-border/50" />
      <Row abbr={g.homeTeam} name={g.homeName} logo={g.homeLogo} score={g.homeScore} win={homeWin} />
    </div>
  );
};

const SeedMini = ({ conf, data }) => (
  <div data-testid={`final-seeds-${conf}`} className="border border-border bg-card">
    <div className="px-4 py-2 bg-secondary border-b border-border label-caps">{conf} Seeds</div>
    {data.seeds.map((s) => (
      <Link
        key={s.team.id}
        to={`/team/${s.team.id}`}
        className="flex items-center gap-3 px-4 py-2 border-b border-border/40 last:border-0 hover:bg-white/5 transition-colors duration-150"
      >
        <span className="font-display text-lg w-6 text-primary">{s.seed}</span>
        <img src={s.team.logo} alt={s.team.abbr} className="w-6 h-6 object-contain" />
        <span className="text-sm font-semibold">{s.team.fullName}</span>
        <span className="ml-auto text-xs text-muted-foreground">{s.team.wins}-{s.team.losses}</span>
      </Link>
    ))}
  </div>
);

export default function Postseason() {
  const { data, isLoading } = useQuery({ queryKey: ["postseason"], queryFn: getPostseason });
  const { data: seeds } = useQuery({ queryKey: ["playoffs"], queryFn: getPlayoffs });
  const champ = data?.champion;

  return (
    <div data-testid="postseason-page" className="space-y-8">
      <div className="flex items-end justify-between border-b-2 border-border pb-2">
        <h1 className="font-display text-5xl">POSTSEASON</h1>
        <span data-testid="postseason-season-label" className="label-caps">{data?.season} Playoff Bracket</span>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading postseason…</p>}

      {champ && (
        <section
          data-testid="champion-banner"
          className="relative border-2 border-primary overflow-hidden"
          style={{ background: "linear-gradient(90deg, hsl(75 100% 10%), rgba(0,0,0,0.92))" }}
        >
          <div className="relative px-6 sm:px-10 py-8 flex items-center gap-6">
            <Trophy className="w-14 h-14 text-primary shrink-0" />
            <img src={champ.logo} alt={champ.abbr} className="w-20 h-20 object-contain" />
            <div>
              <p className="label-caps text-primary">Super Bowl Champions</p>
              <h2 className="font-display text-5xl sm:text-6xl leading-none">{champ.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Won {champ.score} over the {champ.over}
              </p>
            </div>
          </div>
        </section>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {data.rounds.map((rnd) => (
            <div key={rnd.name} data-testid={`round-${rnd.order}`} className="space-y-3">
              <h3 className="font-display text-2xl text-primary border-b border-border pb-1">{rnd.name}</h3>
              {rnd.games.map((g) => (
                <MiniGame key={g.id} g={g} />
              ))}
            </div>
          ))}
        </div>
      )}

      {seeds && (
        <section className="pt-4">
          <h2 className="font-display text-3xl mb-4 border-b-2 border-border pb-2">FINAL SEEDING</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <SeedMini conf="AFC" data={seeds.AFC} />
            <SeedMini conf="NFC" data={seeds.NFC} />
          </div>
        </section>
      )}
    </div>
  );
}
